import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import RichTextEditor from '../RichTextEditor';

const PageEditModal = ({ page, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    title: page?.title || '',
    slug: page?.slug || '',
    content: page?.content || '',
    show_in_menu: page?.show_in_menu || false,
    show_in_footer: page?.show_in_footer || false,
    menu_order: page?.menu_order || 99,
    footer_column: page?.footer_column || 3,
    active: page?.active !== false,
    seo: {
      title: page?.seo?.title || '',
      description: page?.seo?.description || '',
      keywords: page?.seo?.keywords || '',
      og_title: page?.seo?.og_title || '',
      og_description: page?.seo?.og_description || '',
      og_image: page?.seo?.og_image || '',
      twitter_card: page?.seo?.twitter_card || 'summary_large_image',
      canonical_url: page?.seo?.canonical_url || '',
      robots: page?.seo?.robots || 'index, follow'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">{page ? 'Edit Page' : 'Create New Page'}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-2xl">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Page Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">URL Slug *</Label>
              <div className="flex items-center">
                <span className="text-zinc-500 mr-1">/page/</span>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Page Content</Label>
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              placeholder="Write your page content here..."
            />
          </div>

          {/* Display Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.show_in_menu}
                onCheckedChange={(checked) => setFormData({ ...formData, show_in_menu: checked })}
              />
              <Label className="text-zinc-300">Show in Menu</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.show_in_footer}
                onCheckedChange={(checked) => setFormData({ ...formData, show_in_footer: checked })}
              />
              <Label className="text-zinc-300">Show in Footer</Label>
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Menu Order</Label>
              <Input
                type="number"
                value={formData.menu_order}
                onChange={(e) => setFormData({ ...formData, menu_order: parseInt(e.target.value) })}
                className="bg-zinc-700 border-zinc-600 text-white h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Footer Column</Label>
              <select
                value={formData.footer_column}
                onChange={(e) => setFormData({ ...formData, footer_column: parseInt(e.target.value) })}
                className="w-full bg-zinc-700 border border-zinc-600 text-white rounded-md h-8 px-2"
              >
                <option value={1}>Column 1</option>
                <option value={2}>Column 2</option>
                <option value={3}>Column 3</option>
              </select>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="border-t border-zinc-800 pt-6">
            <h3 className="text-white font-medium mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">SEO Title</Label>
                  <Input
                    value={formData.seo.title}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, title: e.target.value } })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Keywords</Label>
                  <Input
                    value={formData.seo.keywords}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, keywords: e.target.value } })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="keyword1, keyword2"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Meta Description</Label>
                <Textarea
                  value={formData.seo.description}
                  onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, description: e.target.value } })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">OG Title</Label>
                  <Input
                    value={formData.seo.og_title}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, og_title: e.target.value } })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">OG Image URL</Label>
                  <Input
                    value={formData.seo.og_image}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, og_image: e.target.value } })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Robots</Label>
                  <select
                    value={formData.seo.robots}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, robots: e.target.value } })}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
                  >
                    <option value="index, follow">Index, Follow</option>
                    <option value="noindex, follow">NoIndex, Follow</option>
                    <option value="index, nofollow">Index, NoFollow</option>
                    <option value="noindex, nofollow">NoIndex, NoFollow</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Canonical URL</Label>
                  <Input
                    value={formData.seo.canonical_url}
                    onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, canonical_url: e.target.value } })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-4">
            <Switch
              checked={formData.active}
              onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
            />
            <Label className="text-zinc-300">Page Active</Label>
            <span className="text-zinc-500 text-sm ml-2">(Inactive pages are not visible to visitors)</span>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Saving...' : (page ? 'Update Page' : 'Create Page')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PageEditModal;
